import { useState, useEffect } from 'react';
import classNames from 'classnames';

import { filterVisibleItems } from 'components/shared/side-menu/side-menu.utils';

import Badge from 'components/partials/badge/badje';
import BaseCollapsedFlyout from './base-collapsed-flyout';
import BaseCollapsedTooltip from './base-collapsed-tooltip';
import BaseNotificationBadge from './base-notification-badge';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import Icons from 'assets/icons';

interface BaseSideMenuItemProps {
  item: NavItem;
  isCollapsed: boolean;
  level?: number;
  inFlyout?: boolean;
  isActive?: boolean;
  isChildActive?: boolean;
  isItemActive?: (item: NavItem) => boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  hasNotification?: boolean;
  onNavigate?: () => void;
}

const BaseSideMenuItem = ({
  item,
  isCollapsed,
  level = 0,
  inFlyout = false,
  isActive: isActiveProp,
  isChildActive: isChildActiveProp,
  isItemActive,
  renderLink,
  hasNotification = false,
  onNavigate,
}: BaseSideMenuItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasChildren = item.children && item.children.length > 0;

  const isItemActiveComputed =
    isActiveProp !== undefined
      ? isActiveProp
      : typeof isItemActive === 'function' && isItemActive(item);

  const isChildActiveComputed =
    isChildActiveProp !== undefined
      ? isChildActiveProp
      : hasChildren &&
        typeof isItemActive === 'function' &&
        (item.children ?? []).some((child) => isItemActive(child));

  useEffect(() => {
    if (isChildActiveComputed && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isChildActiveComputed, isExpanded]);

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    } else if (onNavigate) {
      onNavigate();
    }
  };

  const baseItemClasses = classNames(
    'flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors duration-150',
    'hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset',
    {
      'bg-gray-100 text-gray-700 font-semibold':
        isItemActiveComputed || isChildActiveComputed,
      'text-gray-700 font-semibold text-base':
        !isItemActiveComputed && !isChildActiveComputed,
      'justify-center w-10 h-10 mx-auto':
        isCollapsed && level === 0 && !inFlyout,
      'pl-10': level === 1 && !isCollapsed && !inFlyout,
    }
  );

  const visibleChildren = hasChildren ? filterVisibleItems(item.children ?? []) : [];

  if (isCollapsed && hasChildren && level === 0 && !inFlyout) {
    return (
      <BaseCollapsedFlyout
        item={item}
        baseItemClasses={baseItemClasses}
        renderLink={renderLink}
        isItemActive={isItemActive}
        onNavigate={onNavigate}
      />
    );
  }

  if (isCollapsed && level === 0 && !inFlyout) {
    return (
      <BaseCollapsedTooltip
        item={item}
        baseItemClasses={baseItemClasses}
        isItemActive={isItemActiveComputed}
        renderLink={renderLink}
        hasNotification={hasNotification}
        onNavigate={onNavigate}
      />
    );
  }

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={classNames(baseItemClasses, 'w-full justify-between')}
          aria-expanded={isExpanded}
          aria-haspopup="true"
          data-testid={`side-menu-item-${item.id}`}
        >
          <span className="flex items-center gap-2">
            {item.icon && (
              <span aria-hidden="true">{item.icon}</span>
            )}
            <span>{item.label}</span>
          </span>
          <Icons.Arrow
            customClass={classNames(
              'w-4 h-4 transition-transform duration-200',
              { 'rotate-90': isExpanded }
            )}
            aria-hidden="true"
          />
        </button>
        <div
          className={classNames(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div
            className="mt-1 space-y-1 overflow-hidden"
            role="group"
            aria-label={`${item.label} submenu`}
          >
            {visibleChildren.map((child) => (
              <BaseSideMenuItem
                key={child.id}
                item={child}
                isCollapsed={false}
                level={level + 1}
                isItemActive={isItemActive}
                renderLink={renderLink}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const linkContent = (
    <>
      {item.icon && (
        <span className="relative" aria-hidden="true">
          {item.icon}
          <BaseNotificationBadge hasNotification={hasNotification} />
        </span>
      )}
      <span>{item.label}</span>
      {item.badge && <Badge text={item.badge} variant="neutral-outlined" />}
    </>
  );

  return renderLink({
    to: item.to,
    end: item.exactMatch,
    onClick: onNavigate,
    className: classNames(baseItemClasses, { 'text-sm': inFlyout }),
    'aria-current': isItemActiveComputed ? 'page' : undefined,
    'data-testid': `side-menu-item-${item.id}`,
    children: linkContent,
  });
};

export default BaseSideMenuItem;
